import {
  useLocaleStore,
} from "~/stores/locale";

/**
 * Maps our internal locale codes to BCP 47 language tags for Intl.
 */
const getBCP47Locale = (locale: string): string => {
  switch (locale) {
    case "vn":
      return "vi-VN";
    case "de":
      return "de-DE";
    case "en":
    default:
      return "en-GB";
  }
};

/**
 * Formats a date to a string: "DD MMM YYYY" (e.g., "01 Jan 2024")
 * Respects current locale.
 */
export const formatDate = (date: string | Date | undefined | null) => {
  if (!date) {
    return "";
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return "-";
  }

  const localeStore = useLocaleStore();
  return d.toLocaleDateString(getBCP47Locale(localeStore.currentLocale), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const formatDateInTimeZone = (date: string | Date | undefined | null, timeZone?: string) => {
  if (!date) {
    return "";
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return "-";
  }

  const localeStore = useLocaleStore();
  return d.toLocaleDateString(getBCP47Locale(localeStore.currentLocale), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone,
  });
};

export const formatTimezoneLabel = (timeZone?: string | null) => {
  if (!timeZone) {
    return "";
  }

  const normalized = timeZone.replace(/_/g, " ");
  const segments = normalized.split("/").map((segment) => segment.trim()).filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? normalized;

  return lastSegment
    .split(" ")
    .map((word) => {
      if (!word) {
        return word;
      }
      if (/^(utc|gmt)$/i.test(word)) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

/**
 * Formats a date and time: "DD MMM YYYY, HH:mm"
 * Respects current locale.
 */
export const formatDateTime = (date: string | Date | undefined | null) => {
  if (!date) {
    return "-";
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return "-";
  }

  const localeStore = useLocaleStore();
  return d.toLocaleDateString(getBCP47Locale(localeStore.currentLocale), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Formats a time: "HH:mm" or "HH:mm:ss"
 */
export const formatTime = (date: string | Date | undefined | null, includeSeconds = false) => {
  if (!date) {
    return "-";
  }
  const d = new Date(date);
  if (isNaN(d.getTime())) {
    return "-";
  }

  const localeStore = useLocaleStore();
  return d.toLocaleTimeString(getBCP47Locale(localeStore.currentLocale), {
    hour: "2-digit",
    minute: "2-digit",
    second: includeSeconds ? "2-digit" : undefined,
  });
};

export const formatInclusiveEndDate = (date: string | Date | undefined) => {
  if (!date) {
    return "";
  }
  const d = new Date(date);
  // Subtract 1 day to get the previous calendar day
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString();
};

export const formatMonthYear = (year: number, month: number) => {
  const d = new Date(Date.UTC(year, month, 1));
  const localeStore = useLocaleStore();
  return d.toLocaleDateString(getBCP47Locale(localeStore.currentLocale), {
    month: "long",
    year: "numeric",
  });
};

export const formatPrice = (price: number) => {
  const formattedAmount = new Intl.NumberFormat("vi-VN", {
    maximumFractionDigits: 0,
  }).format(price);

  return `VND ${formattedAmount}`;
};

export const formatNumber = (value: number) => {
  const localeStore = useLocaleStore();
  return new Intl.NumberFormat(getBCP47Locale(localeStore.currentLocale), {
    maximumFractionDigits: 0,
  }).format(value);
};
